/**
 * The one aggregation behind the By Category tab. Inputs are the same raw
 * /api/ld/attendance and /api/ld/cpd-records arrays every other tab reads, and
 * the row rules are trainingStats.js's own (trainer rows excluded, attendance
 * dated by session, CPD by certificate), so a category total here always ties
 * back to the Training Hours tab.
 *
 * Categories NEVER touch the hour arithmetic (display and filter only;
 * mandatory-vs-target hangs off isMandatory alone). This module only answers
 * "where did the hours go" — rows whose category was never filed
 * group under 'Uncategorised' rather than silently vanishing, so the
 * per-category figures always sum to the firm-wide ones.
 */

import { isTraineeRow, inPeriod } from './trainingStats';

export const UNCATEGORISED = 'Uncategorised';

/** 'YYYY-MM-DD' → 'YYYY-MM' bucket key ('' when unparseable). */
const monthKeyOf = (dateStr) => (typeof dateStr === 'string' ? dateStr.slice(0, 7) : '');

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'YYYY-MM' → 'Oct 2025' for chart axes. */
export const monthLabelOf = (key) => {
  const [y, m] = String(key).split('-').map((v) => parseInt(v, 10));
  return MONTH_ABBR[m - 1] ? `${MONTH_ABBR[m - 1]} ${y}` : String(key);
};

/** Every 'YYYY-MM' from start to end inclusive — a month with no training must
 *  show as a zero on the trend, not disappear and shorten the axis. */
export const monthRange = (startKey, endKey) => {
  if (!startKey || !endKey || startKey > endKey) return [];
  const keys = [];
  let [y, m] = startKey.split('-').map((v) => parseInt(v, 10));
  const [ey, em] = endKey.split('-').map((v) => parseInt(v, 10));
  while (y < ey || (y === ey && m <= em)) {
    keys.push(`${y}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m > 12) { m = 1; y += 1; }
    if (keys.length > 240) break; // runaway guard on garbage input
  }
  return keys;
};

const blankCategory = (category) => ({
  category,
  mandatoryHours: 0,
  nonMandatoryHours: 0,
  internalHours: 0,
  externalHours: 0,
  totalHours: 0,
  sessionCodes: new Set(),
  externalRecords: 0,
  participants: new Set(),
  monthly: {}, // 'YYYY-MM' → hours
  gradeHours: {}, // grade → hours
});

/**
 * @param {Array} attendance - /api/ld/attendance rows (or any rows in that shape)
 * @param {Array} cpdRecords - /api/ld/cpd-records rows
 * @param {string} startDate - 'YYYY-MM-DD' or '' for no lower bound
 * @param {string} endDate - 'YYYY-MM-DD' or '' for no upper bound
 */
export function computeCategoryStats(attendance = [], cpdRecords = [], startDate = '', endDate = '') {
  const filteredAttendance = attendance
    .filter((r) => inPeriod(r.date, startDate, endDate))
    .filter(isTraineeRow);
  const filteredCpdRecords = cpdRecords.filter((r) => inPeriod(r.certificateDate, startDate, endDate));

  const byCategory = {};
  const ensure = (title) => {
    const key = title || UNCATEGORISED;
    if (!byCategory[key]) byCategory[key] = blankCategory(key);
    return byCategory[key];
  };

  const grades = new Set();
  const monthKeys = new Set();

  filteredAttendance.forEach((r) => {
    const c = ensure(r.categoryTitle);
    const hours = r.trainingHours || 0;
    if (r.isMandatory) c.mandatoryHours += hours;
    else c.nonMandatoryHours += hours;
    c.internalHours += hours;
    c.totalHours += hours;
    if (r.sessionCode) c.sessionCodes.add(r.sessionCode);
    if (r.email) c.participants.add(r.email);
    const mk = monthKeyOf(r.date);
    if (mk) { c.monthly[mk] = (c.monthly[mk] || 0) + hours; monthKeys.add(mk); }
    const grade = r.grade || 'Other';
    c.gradeHours[grade] = (c.gradeHours[grade] || 0) + hours;
    grades.add(grade);
  });

  filteredCpdRecords.forEach((r) => {
    const c = ensure(r.categoryTitle);
    const hours = r.hours || 0;
    c.externalHours += hours;
    c.totalHours += hours;
    c.externalRecords += 1;
    if (r.participantEmail) c.participants.add(r.participantEmail);
    const mk = monthKeyOf(r.certificateDate);
    if (mk) { c.monthly[mk] = (c.monthly[mk] || 0) + hours; monthKeys.add(mk); }
    const grade = r.grade || 'Other';
    c.gradeHours[grade] = (c.gradeHours[grade] || 0) + hours;
    grades.add(grade);
  });

  // Uncategorised sinks to the bottom regardless of size: it is a data gap to
  // close (filled in as records are edited), not a category competing for
  // budget.
  const categories = Object.values(byCategory)
    .map((c) => ({
      category: c.category,
      mandatoryHours: c.mandatoryHours,
      nonMandatoryHours: c.nonMandatoryHours,
      internalHours: c.internalHours,
      externalHours: c.externalHours,
      totalHours: c.totalHours,
      sessionCount: c.sessionCodes.size,
      externalRecords: c.externalRecords,
      participantCount: c.participants.size,
      avgHoursPerParticipant: c.participants.size > 0 ? c.totalHours / c.participants.size : 0,
      monthly: c.monthly,
      gradeHours: c.gradeHours,
    }))
    .sort((a, b) => (
      (a.category === UNCATEGORISED) - (b.category === UNCATEGORISED)
      || b.totalHours - a.totalHours
      || a.category.localeCompare(b.category)
    ));

  // The month axis spans the filter window when both bounds are set (empty
  // months stay visible as zeros); otherwise just the months with data.
  const months = (startDate && endDate)
    ? monthRange(monthKeyOf(startDate), monthKeyOf(endDate))
    : [...monthKeys].sort();

  const totals = categories.reduce(
    (acc, c) => ({
      mandatoryHours: acc.mandatoryHours + c.mandatoryHours,
      nonMandatoryHours: acc.nonMandatoryHours + c.nonMandatoryHours,
      internalHours: acc.internalHours + c.internalHours,
      externalHours: acc.externalHours + c.externalHours,
      totalHours: acc.totalHours + c.totalHours,
    }),
    { mandatoryHours: 0, nonMandatoryHours: 0, internalHours: 0, externalHours: 0, totalHours: 0 }
  );

  return {
    filteredAttendance,
    filteredCpdRecords,
    categories,
    months,
    grades: [...grades].sort((a, b) => a.localeCompare(b)),
    totals,
  };
}
